<template>
  <b-modal
    v-model="showModal"
    size="lg"
    class="m-4"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submitHandler"
  >
    <template #modal-title>
      <span>{{ details.pob_number }} - <span class="text-primary">{{ details.party_name }}
      </span></span></template>
    <b-form @submit.prevent="submitHandler">
      <b-row>
        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div
              v-if="typeof errorMessage === 'string'"
              class="alert-body"
            >
              <p>{{ errorMessage }}</p>
            </div>
            <div
              v-else-if="errorMessage.detail"
              class="alert-body"
            >
              <p>{{ errorMessage.detail }}</p>
            </div>
            <div
              v-else-if="errorMessage.non_field_errors"
              class="alert-body"
            >
              <p>{{ errorMessage.non_field_errors[0] }}</p>
            </div>
            <div
              v-else-if="errorMessage.message"
              class="alert-body"
            >
              <p>{{ errorMessage.message }}</p>
            </div>
          </b-alert>
        </b-col>

        <!-- Order Confirmation View -->
        <template v-if="showOrderConfirmation">
          <b-col
            cols="12"
            class="text-center py-3"
          >
            <div class="order-confirmation-content">
              <feather-icon
                icon="CheckCircleIcon"
                size="48"
                class="text-success mb-2"
              />
              <h5 class="mb-3">
                POB Status Updated Successfully!
              </h5>
              <p class="mb-3">
                Do you want to create an order from this POB now or later?
              </p>
              <div class="order-amount-display mb-3">
                <small class="text-muted d-block mb-1">Order Amount</small>
                <h5 class="mb-0 text-success font-weight-bold">
                  ₹{{ formateNumber(orderAmount) }}
                </h5>
              </div>
            </div>
          </b-col>
        </template>

        <!-- Regular Form View -->
        <template v-else>
          <!-- Loading State -->
          <b-col
            v-if="loading"
            cols="12"
            class="text-center"
          >
            <b-spinner
              label="Loading..."
              variant="primary"
            />
          </b-col>

          <!-- Content when not loading -->
          <template v-if="!loading">
            <!-- Basic Information Display -->
            <b-col cols="12">
              <b-table-simple
                responsive
                hover
                class="compact-table mb-1"
              >
                <b-tbody>
                  <b-tr>
                    <b-th>Created At:</b-th>
                    <b-td>{{ details.created_at && formatDate(details.created_at) || '-' }}</b-td>
                    <b-th>Created By:</b-th>
                    <b-td>{{ details.created_by_fullname || '-' }}</b-td>
                  </b-tr>
                  <b-tr v-if="details.pob_file">
                    <b-th>Scan of Paper:</b-th>
                    <b-td colspan="3">
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="viewFile"
                      >
                        View File
                      </b-button>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </b-col>
            <!-- MR Information -->
            <b-col cols="12">
              <div
                v-if="details.mr && details.mr.length > 0"
                class="mb-2"
              >
                <h5>Medical Representatives</h5>
                <b-table-simple
                  responsive
                  hover
                  class="compact-table"
                >
                  <b-thead>
                    <b-tr>
                      <b-th>MR Name</b-th>
                      <b-th>Primary Mobile</b-th>
                      <b-th>WhatsApp</b-th>
                      <b-th>Primary Email</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <b-tr
                      v-for="mr in details.mr"
                      :key="mr.id"
                    >
                      <b-td>{{ mr.name }}</b-td>
                      <b-td>
                        {{ (mr.mr_mobiles.find(mobile => mobile.is_primary) && mr.mr_mobiles.find(mobile => mobile.is_primary).mobile) || '-' }}
                      </b-td>
                      <b-td>
                        <span v-if="mr.mr_mobiles.find(mobile => mobile.is_whatsapp)">
                          {{ mr.mr_mobiles.find(mobile => mobile.is_whatsapp).mobile || '-' }}
                        </span>
                        <span v-else>N/A</span>
                      </b-td>
                      <b-td>
                        {{ (mr.mr_emails.find(email => email.is_primary) && mr.mr_emails.find(email => email.is_primary).email) || '-' }}
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
              </div>
            </b-col>
            <!-- Status Selection -->
            <b-col cols="6">
              <b-form-group
                label="Status"
                label-for="status"
              >
                <custom-v-select
                  id="status"
                  v-model="selectedStatus"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="availableStatusOptions"
                  :class="{ 'error-border': validationErrors.status }"
                  label="text"
                  :disabled="!canChangeStatus"
                  @input="handleStatusChange"
                />
                <p
                  v-if="validationErrors.status"
                  class="error-message"
                >
                  {{ validationErrors.status }}
                </p>
              </b-form-group>
            </b-col>

            <!-- POB Items Section -->
            <b-col cols="12">
              <h5 class="mb-1">
                POB Items
              </h5>
              <b-table-simple
                responsive
                hover
                class="compact-table"
              >
                <b-thead>
                  <b-tr>
                    <b-th>Product</b-th>
                    <b-th>Rate (₹)</b-th>
                    <b-th class="text-success">
                      Stock (Live)
                    </b-th>
                    <b-th>Qty</b-th>
                    <b-th>Free Qty</b-th>
                    <b-th>Copy</b-th>
                    <b-th>Approved Qty</b-th>
                    <b-th>Approved Free Qty</b-th>
                    <b-th>Total (₹)</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                    v-for="(item) in pobItems"
                    :key="item.id"
                  >
                    <b-td>{{ item.product }}</b-td>
                    <b-td>{{ item.rate }}</b-td>
                    <b-td class="text-success">
                      {{ item.stock }}
                    </b-td>
                    <b-td>{{ item.quantity }}</b-td>
                    <b-td>{{ item.free_quantity || 0 }}</b-td>
                    <b-td class="text-center">

                      <feather-icon
                        title="Copy to approved"
                        icon="LogOutIcon"
                        size="20"
                        class="cursor-pointer text-primary mr-1"
                        @click="copyQuantities(item)"
                      />
                    </b-td>
                    <b-td>
                      <b-form-input
                        v-model="item.approved_quantity"
                        type="number"
                        min="0"
                        :max="item.quantity"
                        size="sm"
                        :disabled="isRejectionStatus"
                        @input="calculateTotals"
                      />
                    </b-td>
                    <b-td>
                      <b-form-input
                        v-model="item.approved_free_quantity"
                        type="number"
                        min="0"
                        :max="item.free_quantity"
                        size="sm"
                        :disabled="isRejectionStatus"
                        @input="calculateTotals"
                      />
                    </b-td>
                    <b-td>₹{{ ((item.approved_quantity || 0) * item.rate).toFixed(2) }}</b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>

              <div class="d-flex justify-content-between align-items-center">
                <div class="total-order-value">
                  <h6 class="mb-0 text-warning">
                    Total Order Value: <strong>₹{{ totalOrderValue.toFixed(2) }}</strong>
                  </h6>
                </div>
                <div class="final-order-value">
                  <h6 class="mb-0 text-success">
                    Final Order Value: <strong>₹{{ finalOrderValue.toFixed(2) }}</strong>
                  </h6>
                </div>
              </div>
            </b-col>

            <!-- POB Remarks Section -->
            <b-col cols="12">
              <h5 class="mt-3 mb-2">
                {{ remarkLabel }}
              </h5>

              <!-- Existing Remarks (Read-only) -->
              <div
                v-if="existingRemarks.length > 0"
                class="existing-remarks mb-3"
              >
                <div
                  v-for="remark in existingRemarks"
                  :key="`existing-remark-${remark.id}`"
                  class="existing-remark-card mb-2"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <div class="d-flex align-items-center mb-1">
                        <b-badge
                          variant="primary"
                          class="mr-2"
                        >
                          {{ remark.created_by_fullname }}
                        </b-badge>
                        <b-badge
                          :variant="getRemarkTypeVariant(remark.type)"
                          class="mr-2"
                        >
                          {{ formatRemarkType(remark.type) }}
                        </b-badge>
                        <small class="text-muted">
                          {{ formatDate(remark.created_at) }}
                        </small>
                      </div>
                      <p class="mb-0 remark-text">
                        {{ remark.remarks }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Validation error for rejection remarks -->
              <b-col
                v-if="validationErrors.rejection_remark"
                cols="12"
              >
                <b-alert
                  variant="danger"
                  show
                >
                  {{ validationErrors.rejection_remark }}
                </b-alert>
              </b-col>
              <!-- New Remarks (Editable) -->
              <div
                v-for="(remark, index) in newRemarks"
                :key="`new-remark-${remark.tempId || index}`"
              >
                <b-row>
                  <b-col cols="1">
                    <span class="h6">{{ existingRemarks.length + index + 1 }}.</span>
                  </b-col>
                  <b-col cols="10">
                    <b-form-group>
                      <b-form-textarea
                        :id="`remarks-${index}`"
                        v-model="remark.remarks"
                        rows="2"
                        :placeholder="isRejectionStatus ? 'Enter rejection reason (max 28 characters, UPPERCASE)' : 'Enter new remarks'"
                        :maxlength="maxRemarkLength"
                        :class="{ 'error-border': validationErrors[`remark_${index}`] }"
                        @input="handleRemarkInput(remark, index)"
                      />
                      <div
                        v-if="isRejectionStatus"
                        class="d-flex justify-content-between mt-1"
                      >
                        <small class="text-muted">
                          Message preview: "{{ remark.remarks.toUpperCase() }}"
                        </small>
                        <small
                          :class="remark.remarks.length > 28 ? 'text-danger' : 'text-muted'"
                        >
                          {{ remark.remarks.length }}/28 characters
                        </small>
                      </div>
                      <p
                        v-if="validationErrors[`remark_${index}`]"
                        class="error-message"
                      >
                        {{ validationErrors[`remark_${index}`] }}
                      </p>
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="1"
                    class="d-flex align-items-center justify-content-center"
                    style="padding-bottom: 15px;"
                  >
                    <feather-icon
                      v-if="newRemarks.length > 1 && !isRejectionStatus"
                      title="Delete"
                      icon="TrashIcon"
                      size="20"
                      color="red"
                      class="cursor-pointer"
                      @click="removeNewRemark(index)"
                    />
                  </b-col>
                </b-row>
              </div>

              <b-button
                v-if="!isRejectionStatus"
                variant="outline-primary"
                @click="addNewRemark"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-1"
                />
                Add New Remark
              </b-button>
            </b-col>
          </template>
        </template></b-row>
    </b-form>

    <template #modal-footer="{ cancel }">
      <!-- Order Confirmation Buttons -->
      <div v-if="showOrderConfirmation">
        <b-button
          variant="secondary"
          class="mr-1"
          :disabled="orderPlacing"
          @click="handleLater"
        >
          Later
        </b-button>
        <b-button
          variant="success"
          :disabled="orderPlacing"
          @click="handleCreateOrder"
        >
          <b-spinner
            v-if="orderPlacing"
            small
            label="Small Spinner"
          />
          Create Order
        </b-button>
      </div>

      <!-- Regular Form Buttons -->
      <div v-else>
        <b-button
          variant="secondary"
          class="mr-1"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving || !canSave"
          @click="submitHandler"
        >
          Update Status
          <b-spinner
            v-if="saving"
            small
            label="Small Spinner"
          />
        </b-button>
      </div>
    </template>

  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput,
  BForm, BSpinner, BAlert, BFormTextarea, BBadge,
  BTableSimple, BTr, BTh, BTd, BTbody, BThead,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BSpinner,
    BAlert,
    BFormTextarea,
    BBadge,
    BTableSimple,
    BTr,
    BTh,
    BTd,
    BTbody,
    BThead,
  },

  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      showModal: true,
      loading: true,
      details: {},
      selectedStatus: null,
      pobItems: [],
      existingRemarks: [],
      newRemarks: [],
      saving: false,
      errorMessage: {},
      validationErrors: {},
      tempIdCounter: 0,

      // Status options
      allStatusOptions: [
        { value: 'pending', text: 'Pending' },
        { value: 'fully approved', text: 'Fully Approved' },
        { value: 'partially approved', text: 'Partially Approved' },
        { value: 're-approved', text: 'Re-approved' },
        { value: 'rejected', text: 'Rejected' },
      ],

      // Order confirmation view
      showOrderConfirmation: false,
      orderPlacing: false,
      orderAmount: 0,
    }
  },

  computed: {
    mrNames() {
      if (!this.details.mr || this.details.mr.length === 0) return ''
      return this.details.mr.map(mr => mr.name).join(', ')
    },

    availableStatusOptions() {
      if (!this.details.status) return this.allStatusOptions

      const currentStatus = this.details.status

      // If status is pending, show all options
      if (currentStatus === 'pending') {
        return this.allStatusOptions.filter(option => option.value !== 're-approved')
      }

      // If status is rejected, only show re-approved
      if (currentStatus === 'rejected') {
        return this.allStatusOptions.filter(option => option.value === 're-approved')
      }

      // If status is fully approved, partially approved, or re-approved, no changes allowed
      if (['fully approved', 'partially approved', 're-approved'].includes(currentStatus)) {
        return []
      }

      return this.allStatusOptions
    },

    canChangeStatus() {
      return this.availableStatusOptions.length > 0
    },

    canEditApprovedQuantities() {
      return this.selectedStatus && this.selectedStatus.value !== 'rejected'
    },

    canSave() {
      const hasStatusChange = this.selectedStatus && this.selectedStatus.value !== this.details.status
      const hasNewRemarks = this.newRemarks.some(remark => remark.remarks && remark.remarks.trim().length > 0)

      // Check for quantity changes
      const hasQuantityChanges = this.pobItems.some(item => {
        const originalItem = this.details.pob_items?.find(origItem => origItem.id === item.id)
        if (!originalItem) return false

        const currentApprovedQty = parseInt(item.approved_quantity, 10) || 0
        const originalApprovedQty = parseInt(originalItem.approved_quantity, 10) || 0
        const currentApprovedFreeQty = parseInt(item.approved_free_quantity, 10) || 0
        const originalApprovedFreeQty = parseInt(originalItem.approved_free_quantity, 10) || 0

        return currentApprovedQty !== originalApprovedQty || currentApprovedFreeQty !== originalApprovedFreeQty
      })

      return hasStatusChange || hasNewRemarks || hasQuantityChanges
    },

    totalOrderValue() {
      return this.pobItems.reduce((total, item) => {
        const quantity = parseFloat(item.quantity) || 0
        const rate = parseFloat(item.rate) || 0
        return total + (quantity * rate)
      }, 0)
    },

    finalOrderValue() {
      return this.pobItems.reduce((total, item) => {
        const approvedQuantity = parseFloat(item.approved_quantity) || 0
        const rate = parseFloat(item.rate) || 0
        return total + (approvedQuantity * rate)
      }, 0)
    },

    remarkLabel() {
      if (!this.selectedStatus) return 'POB Remarks'

      const status = this.selectedStatus.value
      if (status === 'fully approved') return 'Approval Remarks'
      if (status === 'partially approved') return 'Partial Approval Remarks'
      if (status === 'rejected') return 'Rejection Reason'
      if (status === 're-approved') return 'Re-approval Remarks'
      return 'POB Remarks'
    },

    isRejectionStatus() {
      return this.selectedStatus && this.selectedStatus.value === 'rejected'
    },

    maxRemarkLength() {
      return this.isRejectionStatus ? 28 : null
    },

    shouldShowOrderPopup() {
      // Show order popup only for specific status transitions
      if (!this.selectedStatus || this.selectedStatus.value === this.details.status) {
        return false
      }

      const newStatus = this.selectedStatus.value
      const oldStatus = this.details.status

      // Case 1: Changing TO 'fully approved' or 'partially approved' (from any status)
      if (newStatus === 'fully approved' || newStatus === 'partially approved') {
        return this.orderAmount > 0
      }

      // Case 2: Changing FROM 'rejected' TO 're-approved'
      if (oldStatus === 'rejected' && newStatus === 're-approved') {
        return this.orderAmount > 0
      }

      return false
    },
  },

  async created() {
    await this.fetchPobDetails()
    this.initializeEmptyNewRemarks()
  },

  methods: {
    generateTempId() {
      this.tempIdCounter += 1
      return `temp_${this.tempIdCounter}_${Date.now()}`
    },

    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formateNumber(value) {
      if (!value && value !== 0) return '0'
      return parseFloat(value).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    async fetchPobDetails() {
      try {
        this.loading = true
        const res = await axios.get(`/marg/pobs/${this.id}/`)
        this.details = res.data

        // Set current status
        if (this.details.status) {
          this.selectedStatus = this.allStatusOptions.find(option => option.value === this.details.status)
        }

        // Initialize POB Items
        if (this.details.pob_items && this.details.pob_items.length > 0) {
          this.pobItems = this.details.pob_items.map(item => ({
            id: item.id,
            product: item.product,
            quantity: item.quantity,
            code: item.code,
            rate: parseFloat(item.rate) || 0,
            mrp: parseFloat(item.mrp) || 0,
            stock: parseFloat(item.stock) || 0,
            free_quantity: item.free_quantity || 0,
            approved_quantity: item.approved_quantity || 0,
            approved_free_quantity: item.approved_free_quantity || 0,
          }))
        }

        // Handle POB Remarks
        if (this.details.pob_remarks && this.details.pob_remarks.length > 0) {
          this.existingRemarks = [...this.details.pob_remarks].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        }

        this.loading = false
      } catch (error) {
        this.errorMessage = error?.response?.data || 'Failed to fetch POB details'
        this.loading = false
      }
    },

    copyQuantities(item) {
      // Copy quantity and free_quantity to approved_quantity and approved_free_quantity
      const index = this.pobItems.findIndex(pobItem => pobItem.id === item.id)

      if (index !== -1) {
        this.$set(this.pobItems[index], 'approved_quantity', parseInt(item.quantity, 10) || 0)
        this.$set(this.pobItems[index], 'approved_free_quantity', parseInt(item.free_quantity, 10) || 0)

        // Recalculate totals
        this.calculateTotals()
      }
    },

    handleStatusChange(status) {
      if (!status) return

      // Auto-update approved quantities based on status
      if (status.value === 'fully approved') {
        // Set all approved quantities to original quantities
        this.pobItems.forEach((item, index) => {
          this.$set(this.pobItems[index], 'approved_quantity', item.quantity)
          this.$set(this.pobItems[index], 'approved_free_quantity', item.free_quantity)
        })
      } else if (status.value === 'rejected') {
        // Set all approved quantities to zero
        this.pobItems.forEach((item, index) => {
          this.$set(this.pobItems[index], 'approved_quantity', 0)
          this.$set(this.pobItems[index], 'approved_free_quantity', 0)
        })
      }

      this.calculateTotals()
    },

    calculateTotals() {
      // This method triggers computed property recalculation
      // No explicit calculation needed as computed properties handle it
    },

    addNewRemark() {
      this.newRemarks.push({
        id: null,
        tempId: this.generateTempId(),
        remarks: '',
      })
    },

    removeNewRemark(index) {
      this.newRemarks.splice(index, 1)
    },

    handleRemarkInput(remark, index) {
      // For rejection status, convert to uppercase and validate length
      if (this.isRejectionStatus) {
        // Convert to uppercase using Vue's $set to maintain reactivity
        this.$set(this.newRemarks[index], 'remarks', remark.remarks.toUpperCase())

        // Clear validation error if length is now valid
        if (this.newRemarks[index].remarks.length <= 28 && this.validationErrors[`remark_${index}`]) {
          this.$delete(this.validationErrors, `remark_${index}`)
        }
      }
    },

    getRemarkTypeVariant(type) {
      switch (type) {
        case 'approval': return 'success'
        case 'rejection': return 'danger'
        case 'partial approval': return 'warning'
        case 're-approval': return 'info'
        default: return 'secondary'
      }
    },

    formatRemarkType(type) {
      if (!type) return 'Other'
      return type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')
    },

    initializeEmptyNewRemarks() {
      this.newRemarks = [
        {
          id: null,
          tempId: this.generateTempId(),
          remarks: '',
        },
      ]
    },

    viewFile() {
      if (this.details.pob_file) {
        window.open(this.details.pob_file, '_blank')
      }
    },

    validateForm() {
      this.validationErrors = {}
      let isValid = true

      // Check if status is rejection first (priority validation)
      if (this.isRejectionStatus) {
        const validRejectionRemarks = this.newRemarks.filter(remark => remark.remarks && remark.remarks.trim().length > 0 && remark.remarks.length <= 28)

        if (validRejectionRemarks.length === 0) {
          this.validationErrors.rejection_remark = 'Rejection reason is mandatory and must be max 28 characters'
          isValid = false
        }

        // Validate each rejection remark for length
        this.newRemarks.forEach((remark, index) => {
          if (remark.remarks && remark.remarks.trim().length > 0) {
            if (remark.remarks.length > 28) {
              this.validationErrors[`remark_${index}`] = 'Rejection reason must be max 28 characters'
              isValid = false
            }
          }
        })

        // For rejection status, if we have valid remarks, we don't need other changes
        if (validRejectionRemarks.length > 0) {
          return isValid // Return early if rejection validation passes
        }
      } else {
        // For non-rejection status, check for any type of change
        const hasStatusChange = this.selectedStatus && this.selectedStatus.value !== this.details.status
        const hasNewRemarks = this.newRemarks.some(remark => remark.remarks && remark.remarks.trim().length > 0)

        // Check for quantity changes (approved quantities or approved free quantities)
        const hasQuantityChanges = this.pobItems.some(item => {
          const originalItem = this.details.pob_items?.find(origItem => origItem.id === item.id)
          if (!originalItem) return false

          const currentApprovedQty = parseInt(item.approved_quantity, 10) || 0
          const originalApprovedQty = parseInt(originalItem.approved_quantity, 10) || 0
          const currentApprovedFreeQty = parseInt(item.approved_free_quantity, 10) || 0
          const originalApprovedFreeQty = parseInt(originalItem.approved_free_quantity, 10) || 0

          return currentApprovedQty !== originalApprovedQty || currentApprovedFreeQty !== originalApprovedFreeQty
        })

        if (!hasStatusChange && !hasNewRemarks && !hasQuantityChanges) {
          this.validationErrors.general = 'Please make at least one change (status, quantities, or add remarks)'
          isValid = false
        }
      }

      return isValid
    },

    async submitHandler(event) {
      event.preventDefault()

      if (!this.validateForm()) {
        // Check specific error types for better toast messages - prioritize rejection error
        if (this.validationErrors.rejection_remark) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Rejection Reason Required',
              text: 'Please provide a rejection reason (max 28 characters) when rejecting the POB.',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        } else if (Object.keys(this.validationErrors).some(key => key.startsWith('remark_'))) {
          // Handle individual remark validation errors
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Validation Error',
              text: 'Please correct the highlighted errors in remarks before submitting',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        } else if (this.validationErrors.general) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Validation Error',
              text: 'Please make at least one change before submitting',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        } else {
          // Generic validation error
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Validation Error',
              text: 'Please correct the highlighted errors before submitting',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
        return
      }

      this.errorMessage = {}
      this.saving = true

      try {
        // Create FormData for the same edit-pob API
        const formData = new FormData()

        // Prepare all data as JSON object (combining editable and non-editable fields)
        const dataPayload = {}

        // Include all non-editable detail fields
        if (this.details.company) dataPayload.company = this.details.company
        if (this.details.mr) dataPayload.mr = this.details.mr.map(mr => mr.id)
        if (this.details.party) dataPayload.party = this.details.party
        if (this.details.pob_mode) dataPayload.pob_mode = this.details.pob_mode
        if (this.details.placed_at) dataPayload.placed_at = this.details.placed_at

        // Add editable status (if changed)
        if (this.selectedStatus) {
          dataPayload.status = this.selectedStatus.value
        }
        // Prepare POB Items - include all items with approved quantities
        const allItems = this.pobItems.map(item => {
          const itemData = {
            product: String(item.product),
            code: String(item.code || ''),
            quantity: parseInt(item.quantity, 10) || 0,
            free_quantity: parseInt(item.free_quantity, 10) || 0,
            rate: parseFloat(item.rate) || 0,
            mrp: parseFloat(item.mrp) || 0,
            approved_quantity: parseInt(item.approved_quantity, 10) || 0,
            approved_free_quantity: parseInt(item.approved_free_quantity, 10) || 0,
          }

          // Include ID for existing items
          if (item.id) {
            itemData.id = item.id
          }

          return itemData
        })

        if (allItems.length > 0) {
          dataPayload.pob_items = allItems
        }

        // Prepare POB Remarks - combine existing and new remarks
        const allRemarks = []

        // Add existing remarks (preserve them)
        this.existingRemarks.forEach(remark => {
          allRemarks.push({
            id: remark.id,
            remarks: remark.remarks,
            type: remark.type || 'other',
            created_by: remark.created_by,
            created_at: remark.created_at,
          })
        })

        // Add new remarks with type based on status
        const validNewRemarks = this.newRemarks
          .filter(remark => remark.remarks && remark.remarks.trim().length > 0)
          .map(remark => {
            const remarkData = {
              remarks: remark.remarks.trim(),
            }

            // Determine remark type based on status change
            if (this.selectedStatus && this.selectedStatus.value !== this.details.status) {
              const newStatus = this.selectedStatus.value

              if (newStatus === 'fully approved') {
                remarkData.type = 'approval'
              } else if (newStatus === 'partially approved') {
                remarkData.type = 'partial approval'
              } else if (newStatus === 'rejected') {
                remarkData.type = 'rejection'
              } else if (newStatus === 're-approved') {
                remarkData.type = 're-approval'
              } else {
                remarkData.type = 'other'
              }
            } else {
              // If no status change, default to 'other'
              remarkData.type = 'other'
            }

            return remarkData
          })

        // Combine existing and new remarks
        allRemarks.push(...validNewRemarks)

        if (allRemarks.length > 0) {
          dataPayload.pob_remarks = allRemarks
        }

        // Add data as JSON string
        formData.append('data', JSON.stringify(dataPayload))

        // Handle existing file (preserve it if no changes)
        if (this.details.pob_file && !this.details.remove_file) {
          // Keep existing file - don't add anything to formData for file
        }

        // Send PUT request to the same edit-pob API
        const response = await axios.put(`/marg/edit-pob/${this.id}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json',
          },
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: response.data.message || 'POB Status Updated Successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Calculate order amount
        this.orderAmount = this.pobItems.reduce((sum, item) => {
          const approvedQty = item.approved_quantity || 0
          const rate = parseFloat(item.rate) || 0
          return +((sum + (approvedQty * rate)).toFixed(2))
        }, 0)

        this.saving = false

        // Check if we should show order confirmation
        if (this.shouldShowOrderPopup) {
          this.showOrderConfirmation = true
        } else {
          // If no order confirmation needed, close modal and emit
          this.showModal = false
          this.$emit('updated')
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || 'Something went wrong'
        this.saving = false
      }
    },

    async handleCreateOrder() {
      this.orderPlacing = true

      const payload = {
        pob_id: this.id,
        total_amount: this.orderAmount,
      }

      try {
        await axios.post('/marg/create-pob-order/', payload)

        // Show success toast
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Order Placed',
            text: `Order placed successfully (Amount: ₹${this.formateNumber(this.orderAmount)})`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        this.orderPlacing = false
        this.showOrderConfirmation = false
        this.showModal = false

        // Emit updated event to refresh parent component
        this.$emit('updated')
      } catch (error) {
        this.orderPlacing = false

        // Show error toast
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Order Failed',
            text: error?.response?.data?.error || error?.response?.data?.detail || 'Something went wrong while placing order',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    handleLater() {
      this.showOrderConfirmation = false
      this.showModal = false
      // Emit updated event to refresh parent component
      this.$emit('updated')
    },
  },
}
</script>

<style scoped>
.error-message {
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.error-border {
  border-color: #dc3545 !important;
}

.error-border:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.total-order-value,.final-order-value {
  padding: 8px 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.compact-table th,
.compact-table td {
  padding: 8px 12px;
  vertical-align: middle;
}

.existing-remark-card {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
}

.remark-text {
  color: #495057;
  line-height: 1.5;
  word-wrap: break-word;
}

h5 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.cursor-pointer {
  cursor: pointer;
}

.order-confirmation-content {
  padding: 1rem 0;
}

.order-amount-display {
  padding: 1rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  display: inline-block;
  min-width: 200px;
}
</style>
